import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async'; // <--- NEW
import { useParams, Link } from '../components/Router';
import { fetchListingBySlug, fetchListingById, Listing } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { trackPageView } from '../utils/analytics';
import { 
  MapPin, Fuel, Settings, Calendar, Phone, Mail, ArrowLeft, 
  Image as ImageIcon, Maximize2, X, ChevronLeft, ChevronRight, 
  Gauge, Info, Tag, CheckCircle, Star, Share2, Facebook, Twitter, Linkedin
} from 'lucide-react';

// --- SHARE BUTTON COMPONENT ---
const ShareButton = ({ url, title }: { url: string, title?: string }) => {
  const [isOpen, setIsOpen] = useState(false);
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title || '');

  return (
    <div className="relative z-50">
      <button
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setIsOpen(!isOpen);
        }}
        className="p-2 rounded-full bg-gray-100 text-gray-600 hover:text-blue-600 hover:bg-gray-200 transition-colors"
        title="Partager l'annonce"
      >
        <Share2 className="h-4 w-4" />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 p-2 bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200 shadow-xl flex gap-2 animate-in fade-in zoom-in duration-200 w-max" onClick={(e) => e.stopPropagation()}>
          <a
            href={`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-blue-600 hover:bg-blue-50 rounded-full transition-colors flex items-center justify-center"
            title="Facebook"
          >
            <Facebook className="w-5 h-5" />
          </a>
          <a
            href={`https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-black hover:bg-gray-100 rounded-full transition-colors flex items-center justify-center"
            title="X (Twitter)"
          >
            <Twitter className="w-5 h-5" />
          </a>
          <a
            href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 text-blue-700 hover:bg-blue-50 rounded-full transition-colors flex items-center justify-center"
            title="LinkedIn"
          >
            <Linkedin className="w-5 h-5" />
          </a>
        </div>
      )}
    </div>
  );
};

export function ListingDetailsPage() {
  const params = useParams();
  const identifier = params.slug || params.id || '';
  
  const { user } = useAuth();
  const [listing, setListing] = useState<Listing | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isGalleryOpen, setIsGalleryOpen] = useState(false);

  useEffect(() => {
    if (identifier) {
      trackPageView(`/annonces/${identifier}`);
      loadListingData(identifier);
    } else {
      setError('Aucun identifiant d\'annonce fourni');
      setLoading(false);
    }
  }, [identifier, user]);

  const loadListingData = async (idOrSlug: string) => {
    try {
      setLoading(true);
      setError(null);
      const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(idOrSlug);
      
      let data;
      if (isUUID) {
        data = await fetchListingById(idOrSlug, user?.id);
      } else {
        data = await fetchListingBySlug(idOrSlug, user?.id);
      }
      
      if (!data) {
        setError('Annonce non trouvée ou supprimée');
      } else {
        setListing(data);
      }
    } catch (error) {
      console.error('Error loading listing:', error);
      setError('Erreur lors du chargement de l\'annonce');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (listing && listing.images && listing.images.length > 0) {
      setCurrentImageIndex((prev) => prev === 0 ? listing.images.length - 1 : prev - 1);
    }
  };

  const handleNextImage = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (listing && listing.images && listing.images.length > 0) {
      setCurrentImageIndex((prev) => prev === listing.images.length - 1 ? 0 : prev + 1);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isGalleryOpen) return;
      if (e.key === 'ArrowLeft') handlePrevImage();
      if (e.key === 'ArrowRight') handleNextImage();
      if (e.key === 'Escape') setIsGalleryOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isGalleryOpen, listing]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4452C] mx-auto"></div>
          <p className="mt-3 text-gray-500 text-sm font-medium">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center p-4">
        <div className="text-center bg-white/80 backdrop-blur-xl p-8 rounded-3xl border border-gray-200 max-w-sm w-full">
          <Info className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 mb-1">Annonce indisponible</h3>
          <p className="text-sm text-gray-500 mb-6">{error || 'Cette annonce n\'existe plus.'}</p>
          <Link to="/annonces" className="inline-flex items-center justify-center w-full bg-[#D5462E] text-white px-6 py-3 rounded-full hover:bg-[#b83a24] transition-all text-sm font-semibold">
            Retour aux annonces
          </Link>
        </div>
      </div>
    );
  }

  // META DATA VARIABLES
  const pageTitle = `${listing.brand} ${listing.model} (${listing.year})`;
  const pageDescription = listing.description ? listing.description.substring(0, 150).replace(/\n/g, ' ') + '...' : `Découvrez cette ${listing.brand} ${listing.model} en vente sur Argus Tunisie.`;
  const shareUrl = window.location.href;
  const ogImage = (listing.images && listing.images.length > 0) ? listing.images[0] : 'https://www.argusautomobile.com/og-image-default.jpg';

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* --- ADDED HELMET TAGS FOR SEO & SHARING --- */}
      <Helmet>
        <title>{pageTitle} | Argus Tunisie</title>
        <meta name="description" content={pageDescription} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${pageTitle} - ${listing.price.toLocaleString()} TND`} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={shareUrl} />
        <meta property="og:site_name" content="Argus Tunisie" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={ogImage} />
      </Helmet>

      {/* Full Screen Lightbox Modal */}
      {isGalleryOpen && listing.images && (
        <div className="fixed inset-0 z-50 bg-black/95 flex flex-col justify-center items-center backdrop-blur-sm animate-in fade-in duration-200">
          <button onClick={() => setIsGalleryOpen(false)} className="absolute top-4 right-4 p-2 text-white/70 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all">
            <X className="h-6 w-6" />
          </button>
          
          <div className="relative w-full h-full max-w-6xl max-h-[90vh] px-4 flex items-center justify-center">
            <img src={listing.images[currentImageIndex]} alt={`Full screen view ${currentImageIndex + 1}`} className="max-w-full max-h-full object-contain" />
            
            {listing.images.length > 1 && (
              <>
                <button onClick={handlePrevImage} className="absolute left-2 lg:left-4 p-2 text-white bg-black/50 hover:bg-[#D5462E] rounded-full transition-all">
                  <ChevronLeft className="h-6 w-6" />
                </button>
                <button onClick={handleNextImage} className="absolute right-2 lg:right-4 p-2 text-white bg-black/50 hover:bg-[#D5462E] rounded-full transition-all">
                  <ChevronRight className="h-6 w-6" />
                </button>
              </>
            )}
          </div>
          <div className="absolute bottom-4 text-white/80 text-xs font-medium tracking-wider bg-black/40 px-3 py-1 rounded-full">
            {currentImageIndex + 1} / {listing.images.length}
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3">
          <div className="flex justify-between items-center">
            <Link to="/annonces" className="inline-flex items-center text-gray-500 hover:text-[#D5462E] transition-colors text-xs font-medium uppercase tracking-wide">
              <ArrowLeft className="h-3 w-3 mr-1.5" />
              Retour
            </Link>
            {/* ADDED SHARE BUTTON */}
            <ShareButton url={shareUrl} title={pageTitle} />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* ... (Rest of the component content - Header, Gallery, Specs, etc. remains same as previous working version) ... */}
        
        {/* Header Block */}
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-semibold text-gray-900 tracking-tight">
            {listing.brand} <span className="text-gray-600 font-normal">{listing.model}</span>
          </h1>
          <div className="flex items-center mt-2 text-gray-500 space-x-3 text-xs lg:text-sm">
            <div className="flex items-center"><MapPin className="h-3.5 w-3.5 mr-1 text-[#D5462E]" />{listing.location}</div>
            <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
            <div className="flex items-center"><Calendar className="h-3.5 w-3.5 mr-1 text-gray-400" />{listing.year}</div>
          </div>
          
          <div className="mt-4 lg:hidden">
            <p className="text-2xl font-semibold text-[#D5462E]">{listing.price.toLocaleString()} TND</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          
          {/* LEFT COLUMN */}
          <div className="lg:col-span-2 space-y-6">
            
            {/* Gallery Viewer */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 overflow-hidden">
              <div className="relative aspect-video bg-gray-100 group">
                {listing.images && listing.images.length > 0 ? (
                  <>
                    <img src={listing.images[currentImageIndex]} alt={`${listing.brand} ${listing.model}`} className="w-full h-full object-cover cursor-pointer" onClick={() => setIsGalleryOpen(true)} />
                    <div className="absolute top-3 right-3">
                      <button onClick={() => setIsGalleryOpen(true)} className="bg-white/90 hover:bg-white text-gray-800 p-2 rounded-2xl border border-white/20 transition-all">
                        <Maximize2 className="h-4 w-4" />
                      </button>
                    </div>

                    {listing.images.length > 1 && (
                      <>
                        <button onClick={handlePrevImage} className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <ChevronLeft className="h-5 w-5 text-gray-800" />
                        </button>
                        <button onClick={handleNextImage} className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-1.5 rounded-full border border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                          <ChevronRight className="h-5 w-5 text-gray-800" />
                        </button>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-gray-400">
                    <ImageIcon className="h-10 w-10 mb-2 opacity-50" />
                    <span className="text-xs">Pas d'image</span>
                  </div>
                )}
              </div>

              {listing.images && listing.images.length > 1 && (
                <div className="p-4 border-t border-gray-200/50 bg-white/50 overflow-x-auto">
                  <div className="flex space-x-2">
                    {listing.images.map((image, index) => (
                      <button key={index} onClick={() => setCurrentImageIndex(index)} className={`relative flex-shrink-0 w-14 h-10 rounded-xl overflow-hidden transition-all ${index === currentImageIndex ? 'ring-2 ring-[#D5462E] ring-offset-2 opacity-100' : 'opacity-60 hover:opacity-100 border border-gray-200'}`}>
                        <img src={image} alt={`Thumb ${index}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Specifications Grid */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 p-8">
              <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wide flex items-center">
                <Settings className="h-4 w-4 text-[#D5462E] mr-2" />Détails
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6">
                <SpecItem icon={<Calendar className="h-4 w-4" />} label="Année" value={listing.year.toString()} />
                <SpecItem icon={<Gauge className="h-4 w-4" />} label="Kilométrage" value={`${listing.mileage.toLocaleString()} km`} />
                <SpecItem icon={<Fuel className="h-4 w-4" />} label="Carburant" value={listing.fuel_type} />
                <SpecItem icon={<Settings className="h-4 w-4" />} label="Boîte" value={listing.transmission} />
                {listing.color && <SpecItem icon={<div className="w-4 h-4 rounded-full border border-gray-300" style={{backgroundColor: 'currentColor'}}></div>} label="Couleur" value={listing.color} />}
                {listing.condition && <SpecItem icon={<Star className="h-4 w-4" />} label="État" value={listing.condition} />}
              </div>
            </div>

            {/* NEW: Equipments & Options */}
            {listing.features && listing.features.length > 0 && (
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 p-8">
                <h2 className="text-sm font-semibold text-gray-900 mb-6 uppercase tracking-wide flex items-center">
                  <CheckCircle className="h-4 w-4 text-[#D5462E] mr-2" />
                  Équipements et Options
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-6">
                  {listing.features.map((feature: string, index: number) => (
                    <div key={index} className="flex items-center text-sm text-gray-700">
                      <div className="w-4 h-4 rounded-full bg-green-50 border border-green-200 flex items-center justify-center mr-3 flex-shrink-0">
                        <CheckCircle className="h-2.5 w-2.5 text-green-600" />
                      </div>
                      {feature}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Description */}
            <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 p-8">
              <h2 className="text-sm font-semibold text-gray-900 mb-4 uppercase tracking-wide">Description</h2>
              <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed whitespace-pre-wrap text-sm">
                {listing.description}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN - Sticky Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-20 space-y-6">
              
              <div className="bg-white/80 backdrop-blur-xl rounded-3xl border border-gray-200 p-8">
                
                <div className="hidden lg:block border-b border-gray-200 pb-6 mb-6">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Prix demandé</span>
                    <Tag className="h-4 w-4 text-[#D5462E]" />
                  </div>
                  <div className="text-3xl font-semibold text-[#D5462E] tracking-tight">{listing.price.toLocaleString()} TND</div>
                </div>

                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-sm font-semibold text-gray-900">Vendeur</h2>
                  <span className="bg-green-50 text-green-700 text-[10px] font-semibold px-2 py-1 rounded-full border border-green-100 uppercase">Vérifié</span>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-2xl border border-gray-100">
                    <Phone className="h-4 w-4 text-gray-500 mt-0.5" />
                    <div className="overflow-hidden">
                      <p className="text-[10px] text-gray-500 font-semibold uppercase">Téléphone</p>
                      <a href={`tel:${listing.contact_phone}`} className="text-sm text-gray-900 font-medium hover:text-[#D5462E] truncate block">{listing.contact_phone}</a>
                    </div>
                  </div>

                  <div className="flex items-start space-x-3 p-3 bg-gray-50 rounded-2xl border border-gray-100">
                    <Mail className="h-4 w-4 text-gray-500 mt-0.5" />
                    <div className="overflow-hidden">
                      <p className="text-[10px] text-gray-500 font-semibold uppercase">Email</p>
                      <a href={`mailto:${listing.contact_email}`} className="text-sm text-gray-900 font-medium hover:text-[#D5462E] truncate block">{listing.contact_email}</a>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  <a href={`tel:${listing.contact_phone}`} className="flex items-center justify-center w-full bg-[#D5462E] text-white py-3 rounded-full text-sm font-semibold hover:bg-[#b83a24] transition-all">
                    <Phone className="h-3.5 w-3.5 mr-2" /> Appeler
                  </a>
                  <a href={`mailto:${listing.contact_email}`} className="flex items-center justify-center w-full bg-white border border-gray-200 text-gray-700 py-3 rounded-full text-sm font-semibold hover:border-[#D5462E] hover:text-[#D5462E] transition-all">
                    <Mail className="h-3.5 w-3.5 mr-2" /> Envoyer un message
                  </a>
                </div>
              </div>

              <div className="bg-blue-50/50 backdrop-blur-xl border border-blue-100 rounded-3xl p-6">
                <p className="text-sm text-blue-800 leading-normal mb-4 font-medium">
                  <strong className="font-semibold">Expertise Professionnelle:</strong> Faites diagnostiquer le véhicule par nos experts avant votre achat pour une tranquillité d'esprit totale.
                </p>
                <Link to="/contact" className="inline-flex items-center bg-blue-600 text-white px-5 py-2.5 rounded-full hover:bg-blue-700 transition-colors text-sm font-semibold">
                  Demander une expertise
                </Link>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function SpecItem({ icon, label, value }: { icon: React.ReactNode, label: string, value: string }) {
  return (
    <div className="flex items-start space-x-3">
      <div className="p-2 bg-gray-50 rounded-xl border border-gray-100 text-gray-500">{icon}</div>
      <div>
        <p className="text-[10px] text-gray-500 font-semibold uppercase tracking-wide mb-1">{label}</p>
        <p className="text-sm text-gray-900 font-medium">{value}</p>
      </div>
    </div>
  );
}